import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Truck,
    Package,
    Plus,
    Search,
    Edit,
    Trash2,
    Phone,
    Mail,
    MapPin,
    Calendar,
    DollarSign,
    TrendingUp,
    ShoppingCart,
    Clock,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    FileText,
    Building2
} from 'lucide-react';

const SuppliersPage = () => {
    const { api, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [suppliers, setSuppliers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [dashboardStats, setDashboardStats] = useState(null);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [activeTab, setActiveTab] = useState('suppliers');
    
    // Dialogs
    const [supplierDialog, setSupplierDialog] = useState(false);
    const [orderDialog, setOrderDialog] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [editingOrder, setEditingOrder] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [detailDialog, setDetailDialog] = useState(false);
    
    const canManage = ['DIRECTEUR', 'ADMIN'].includes(user?.role);
    
    // Form states
    const [supplierForm, setSupplierForm] = useState({
        name: '',
        contact_name: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        category: 'general',
        payment_terms: 'comptant',
        min_order_amount: 0,
        notes: ''
    });
    
    const [orderForm, setOrderForm] = useState({
        supplier_id: '',
        order_date: new Date().toISOString().split('T')[0],
        expected_delivery_date: '',
        items: [],
        total_amount: 0,
        notes: '',
        status: 'pending'
    });
    
    const [newItem, setNewItem] = useState({ name: '', quantity: 1, unit: 'kg', unit_price: 0 });
    
    const categoryIcons = {
        general: Package,
        viandes: Package,
        poissons: Package,
        legumes: Package,
        boissons: Package,
        epicerie: Package,
        laitiers: Package,
        autres: Package
    };
    
    const fetchData = useCallback(async () => {
        try {
            const [suppliersRes, ordersRes, statsRes, categoriesRes] = await Promise.all([
                api().get('/suppliers'),
                api().get('/suppliers/orders/list'),
                api().get('/suppliers/dashboard/stats'),
                api().get('/suppliers/categories')
            ]);
            setSuppliers(suppliersRes.data);
            setOrders(ordersRes.data);
            setDashboardStats(statsRes.data);
            setCategories(categoriesRes.data.categories || []);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [api]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    // Supplier handlers
    const handleOpenSupplier = (supplier = null) => {
        if (supplier) {
            setEditingSupplier(supplier);
            setSupplierForm({
                name: supplier.name,
                contact_name: supplier.contact_name || '',
                phone: supplier.phone || '',
                email: supplier.email || '',
                address: supplier.address || '',
                city: supplier.city || '',
                category: supplier.category || 'general',
                payment_terms: supplier.payment_terms || 'comptant',
                min_order_amount: supplier.min_order_amount || 0,
                notes: supplier.notes || ''
            });
        } else {
            setEditingSupplier(null);
            setSupplierForm({
                name: '',
                contact_name: '',
                phone: '',
                email: '',
                address: '',
                city: '',
                category: 'general',
                payment_terms: 'comptant',
                min_order_amount: 0,
                notes: ''
            });
        }
        setSupplierDialog(true);
    };
    
    const handleSaveSupplier = async () => {
        if (!supplierForm.name.trim()) {
            toast.error('Le nom est requis');
            return;
        }
        
        try {
            if (editingSupplier) {
                await api().put(`/suppliers/${editingSupplier.id}`, supplierForm);
                toast.success('Fournisseur modifié');
            } else {
                await api().post('/suppliers', supplierForm);
                toast.success('Fournisseur créé');
            }
            setSupplierDialog(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    const handleDeleteSupplier = async (supplierId) => {
        if (!window.confirm('Désactiver ce fournisseur ?')) return;
        try {
            await api().delete(`/suppliers/${supplierId}`);
            toast.success('Fournisseur désactivé');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    const handleViewSupplier = async (supplier) => {
        try {
            const res = await api().get(`/suppliers/${supplier.id}`);
            setSelectedSupplier(res.data);
            setDetailDialog(true);
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    // Order handlers
    const handleOpenOrder = (order = null, supplier = null) => {
        if (order) {
            setEditingOrder(order);
            setOrderForm({
                supplier_id: order.supplier_id,
                order_date: order.order_date,
                expected_delivery_date: order.expected_delivery_date || '',
                items: order.items || [],
                total_amount: order.total_amount || 0,
                notes: order.notes || '',
                status: order.status
            });
        } else {
            setEditingOrder(null);
            setOrderForm({
                supplier_id: supplier?.id || '',
                order_date: new Date().toISOString().split('T')[0],
                expected_delivery_date: '',
                items: [],
                total_amount: 0,
                notes: '',
                status: 'pending'
            });
        }
        setOrderDialog(true);
    };
    
    const handleAddItem = () => {
        if (!newItem.name.trim()) return;
        const items = [...orderForm.items, { ...newItem }];
        const total = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
        setOrderForm({ ...orderForm, items, total_amount: total });
        setNewItem({ name: '', quantity: 1, unit: 'kg', unit_price: 0 });
    };
    
    const handleRemoveItem = (index) => {
        const items = orderForm.items.filter((_, i) => i !== index);
        const total = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
        setOrderForm({ ...orderForm, items, total_amount: total });
    };
    
    const handleSaveOrder = async () => {
        if (!orderForm.supplier_id) {
            toast.error('Sélectionnez un fournisseur');
            return;
        }
        
        try {
            if (editingOrder) {
                await api().put(`/suppliers/orders/${editingOrder.id}`, orderForm);
                toast.success('Commande modifiée');
            } else {
                await api().post('/suppliers/orders', orderForm);
                toast.success('Commande créée');
            }
            setOrderDialog(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    const handleUpdateOrderStatus = async (orderId, status) => {
        try {
            await api().put(`/suppliers/orders/${orderId}/status?status=${status}`);
            toast.success('Statut mis à jour');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    // Filters
    const filteredSuppliers = suppliers.filter(s => {
        const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (s.contact_name || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'all' || s.category === filterCategory;
        return matchesSearch && matchesCategory;
    });
    
    const getStatusBadge = (status) => {
        const config = {
            pending: { color: 'bg-yellow-100 text-yellow-700', label: 'En attente', icon: Clock },
            ordered: { color: 'bg-blue-100 text-blue-700', label: 'Commandée', icon: ShoppingCart },
            delivered: { color: 'bg-green-100 text-green-700', label: 'Livrée', icon: CheckCircle2 },
            partial: { color: 'bg-orange-100 text-orange-700', label: 'Partielle', icon: AlertTriangle },
            cancelled: { color: 'bg-red-100 text-red-700', label: 'Annulée', icon: XCircle }
        };
        const c = config[status] || config.pending;
        const Icon = c.icon;
        return (
            <Badge className={c.color}>
                <Icon className="w-3 h-3 mr-1" />
                {c.label}
            </Badge>
        );
    };
    
    const getCategoryLabel = (catId) => {
        const cat = categories.find(c => c.id === catId);
        return cat?.name || catId;
    };
    
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR').format(value || 0) + ' FCFA';
    };
    
    const stats = dashboardStats || {};
    
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }
    
    return (
        <div className="space-y-6" data-testid="suppliers-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Fournisseurs</h1>
                    <p className="text-muted-foreground">Gestion des fournisseurs et commandes</p>
                </div>
                {canManage && (
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => handleOpenOrder()}>
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Nouvelle commande
                        </Button>
                        <Button onClick={() => handleOpenSupplier()} data-testid="add-supplier-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Nouveau fournisseur
                        </Button>
                    </div>
                )}
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4 text-center">
                        <Building2 className="w-6 h-6 mx-auto text-muted-foreground mb-1" />
                        <div className="text-2xl font-bold">{stats.total_suppliers || 0}</div>
                        <div className="text-xs text-muted-foreground">Fournisseurs</div>
                    </CardContent>
                </Card>
                <Card className="bg-blue-50 dark:bg-blue-900/20">
                    <CardContent className="p-4 text-center">
                        <ShoppingCart className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                        <div className="text-2xl font-bold text-blue-700">{stats.orders_this_month || 0}</div>
                        <div className="text-xs text-muted-foreground">Commandes ce mois</div>
                    </CardContent>
                </Card>
                <Card className="bg-green-50 dark:bg-green-900/20">
                    <CardContent className="p-4 text-center">
                        <DollarSign className="w-6 h-6 mx-auto text-green-600 mb-1" />
                        <div className="text-xl font-bold text-green-700">{formatCurrency(stats.total_this_month)}</div>
                        <div className="text-xs text-muted-foreground">Total ce mois</div>
                    </CardContent>
                </Card>
                <Card className="bg-yellow-50 dark:bg-yellow-900/20">
                    <CardContent className="p-4 text-center">
                        <Clock className="w-6 h-6 mx-auto text-yellow-600 mb-1" />
                        <div className="text-2xl font-bold text-yellow-700">{stats.pending_orders || 0}</div>
                        <div className="text-xs text-muted-foreground">En attente</div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="suppliers">
                        <Building2 className="w-4 h-4 mr-2" />
                        Fournisseurs
                    </TabsTrigger>
                    <TabsTrigger value="orders">
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Commandes
                    </TabsTrigger>
                </TabsList>
                
                {/* Suppliers Tab */}
                <TabsContent value="suppliers" className="mt-4">
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Rechercher..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10"
                                    />
                                </div>
                                <Select value={filterCategory} onValueChange={setFilterCategory}>
                                    <SelectTrigger className="w-48">
                                        <SelectValue placeholder="Catégorie" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Toutes les catégories</SelectItem>
                                        {categories.map(cat => (
                                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {filteredSuppliers.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>Aucun fournisseur trouvé</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {filteredSuppliers.map(supplier => (
                                        <div 
                                            key={supplier.id}
                                            className="p-4 border rounded-lg hover:border-primary transition-colors cursor-pointer"
                                            onClick={() => handleViewSupplier(supplier)}
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <h3 className="font-semibold">{supplier.name}</h3>
                                                    <Badge variant="outline" className="text-xs">
                                                        {getCategoryLabel(supplier.category)}
                                                    </Badge>
                                                </div>
                                                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                                    <Button variant="ghost" size="sm" onClick={() => handleOpenSupplier(supplier)}>
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            
                                            {supplier.contact_name && (
                                                <p className="text-sm text-muted-foreground mb-1">{supplier.contact_name}</p>
                                            )}
                                            
                                            <div className="text-xs text-muted-foreground space-y-1">
                                                {supplier.phone && (
                                                    <div className="flex items-center gap-1">
                                                        <Phone className="w-3 h-3" />
                                                        {supplier.phone}
                                                    </div>
                                                )}
                                                {supplier.email && (
                                                    <div className="flex items-center gap-1">
                                                        <Mail className="w-3 h-3" />
                                                        {supplier.email}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="mt-3 pt-3 border-t flex justify-between text-xs">
                                                <span>{supplier.orders_this_month || 0} cmd ce mois</span>
                                                <span className="font-medium">{formatCurrency(supplier.total_this_month)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* Orders Tab */}
                <TabsContent value="orders" className="mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Historique des commandes</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {orders.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>Aucune commande</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Fournisseur</TableHead>
                                            <TableHead>Articles</TableHead>
                                            <TableHead>Montant</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {orders.map(order => (
                                            <TableRow key={order.id}>
                                                <TableCell>{order.order_date}</TableCell>
                                                <TableCell className="font-medium">{order.supplier_name}</TableCell>
                                                <TableCell>{order.items?.length || 0} articles</TableCell>
                                                <TableCell>{formatCurrency(order.total_amount)}</TableCell>
                                                <TableCell>{getStatusBadge(order.status)}</TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-1">
                                                        {order.status === 'pending' && (
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm"
                                                                onClick={() => handleUpdateOrderStatus(order.id, 'ordered')}
                                                                className="text-blue-600"
                                                            >
                                                                <ShoppingCart className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                        {order.status === 'ordered' && (
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm"
                                                                onClick={() => handleUpdateOrderStatus(order.id, 'delivered')}
                                                                className="text-green-600"
                                                            >
                                                                <CheckCircle2 className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                        <Button variant="ghost" size="sm" onClick={() => handleOpenOrder(order)}>
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
            
            {/* Supplier Dialog */}
            <Dialog open={supplierDialog} onOpenChange={setSupplierDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingSupplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                        <div className="space-y-2">
                            <Label>Nom de l'entreprise *</Label>
                            <Input
                                value={supplierForm.name}
                                onChange={(e) => setSupplierForm({...supplierForm, name: e.target.value})}
                                placeholder="Nom du fournisseur"
                                data-testid="supplier-name-input"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Catégorie</Label>
                                <Select value={supplierForm.category} onValueChange={(v) => setSupplierForm({...supplierForm, category: v})}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map(cat => (
                                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Conditions paiement</Label>
                                <Select value={supplierForm.payment_terms} onValueChange={(v) => setSupplierForm({...supplierForm, payment_terms: v})}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="comptant">Comptant</SelectItem>
                                        <SelectItem value="30_jours">30 jours</SelectItem>
                                        <SelectItem value="60_jours">60 jours</SelectItem>
                                        <SelectItem value="fin_mois">Fin de mois</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Personne de contact</Label>
                            <Input
                                value={supplierForm.contact_name}
                                onChange={(e) => setSupplierForm({...supplierForm, contact_name: e.target.value})}
                                placeholder="Nom du contact"
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Téléphone</Label>
                                <Input
                                    value={supplierForm.phone}
                                    onChange={(e) => setSupplierForm({...supplierForm, phone: e.target.value})}
                                    placeholder="+241..."
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={supplierForm.email}
                                    onChange={(e) => setSupplierForm({...supplierForm, email: e.target.value})}
                                    placeholder="email@exemple.com"
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Adresse</Label>
                                <Input
                                    value={supplierForm.address}
                                    onChange={(e) => setSupplierForm({...supplierForm, address: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Ville</Label>
                                <Input
                                    value={supplierForm.city}
                                    onChange={(e) => setSupplierForm({...supplierForm, city: e.target.value})}
                                    placeholder="Libreville"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Commande minimum (FCFA)</Label>
                            <Input
                                type="number"
                                min="0"
                                value={supplierForm.min_order_amount}
                                onChange={(e) => setSupplierForm({...supplierForm, min_order_amount: parseInt(e.target.value) || 0})}
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Textarea
                                value={supplierForm.notes}
                                onChange={(e) => setSupplierForm({...supplierForm, notes: e.target.value})}
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSupplierDialog(false)}>Annuler</Button>
                        <Button onClick={handleSaveSupplier} data-testid="save-supplier-btn">
                            {editingSupplier ? 'Modifier' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Order Dialog */}
            <Dialog open={orderDialog} onOpenChange={setOrderDialog}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editingOrder ? 'Modifier la commande' : 'Nouvelle commande'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Fournisseur *</Label>
                                <Select value={orderForm.supplier_id} onValueChange={(v) => setOrderForm({...orderForm, supplier_id: v})}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Sélectionner" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {suppliers.map(s => (
                                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Date de commande</Label>
                                <Input
                                    type="date"
                                    value={orderForm.order_date}
                                    onChange={(e) => setOrderForm({...orderForm, order_date: e.target.value})}
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Date de livraison prévue</Label>
                            <Input
                                type="date"
                                value={orderForm.expected_delivery_date}
                                onChange={(e) => setOrderForm({...orderForm, expected_delivery_date: e.target.value})}
                            />
                        </div>
                        
                        {/* Items */}
                        <div className="space-y-2">
                            <Label>Articles</Label>
                            <div className="border rounded-lg p-3 space-y-2">
                                <div className="grid grid-cols-5 gap-2">
                                    <Input
                                        placeholder="Article"
                                        value={newItem.name}
                                        onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                                        className="col-span-2"
                                    />
                                    <Input
                                        type="number"
                                        placeholder="Qté"
                                        value={newItem.quantity}
                                        onChange={(e) => setNewItem({...newItem, quantity: parseFloat(e.target.value) || 0})}
                                    />
                                    <Input
                                        type="number"
                                        placeholder="Prix unit."
                                        value={newItem.unit_price}
                                        onChange={(e) => setNewItem({...newItem, unit_price: parseFloat(e.target.value) || 0})}
                                    />
                                    <Button onClick={handleAddItem} size="sm">
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                                
                                {orderForm.items.length > 0 && (
                                    <div className="space-y-1 mt-2">
                                        {orderForm.items.map((item, idx) => (
                                            <div key={idx} className="flex justify-between items-center text-sm bg-muted/50 px-2 py-1 rounded">
                                                <span>{item.name} x {item.quantity}</span>
                                                <div className="flex items-center gap-2">
                                                    <span>{formatCurrency(item.quantity * item.unit_price)}</span>
                                                    <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(idx)}>
                                                        <Trash2 className="w-3 h-3 text-red-500" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="flex justify-between font-medium pt-2 border-t">
                                            <span>Total</span>
                                            <span>{formatCurrency(orderForm.total_amount)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Textarea
                                value={orderForm.notes}
                                onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})}
                                rows={2}
                            />
                        </div>
                        
                        {editingOrder && (
                            <div className="space-y-2">
                                <Label>Statut</Label>
                                <Select value={orderForm.status} onValueChange={(v) => setOrderForm({...orderForm, status: v})}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pending">En attente</SelectItem>
                                        <SelectItem value="ordered">Commandée</SelectItem>
                                        <SelectItem value="delivered">Livrée</SelectItem>
                                        <SelectItem value="partial">Livraison partielle</SelectItem>
                                        <SelectItem value="cancelled">Annulée</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOrderDialog(false)}>Annuler</Button>
                        <Button onClick={handleSaveOrder} data-testid="save-order-btn">
                            {editingOrder ? 'Modifier' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Supplier Detail Dialog */}
            <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{selectedSupplier?.name}</DialogTitle>
                    </DialogHeader>
                    {selectedSupplier && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-muted-foreground">Catégorie</p>
                                    <Badge variant="outline">{getCategoryLabel(selectedSupplier.category)}</Badge>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Conditions</p>
                                    <p className="font-medium">{selectedSupplier.payment_terms || 'Comptant'}</p>
                                </div>
                            </div>
                            
                            {selectedSupplier.contact_name && (
                                <div>
                                    <p className="text-sm text-muted-foreground">Contact</p>
                                    <p className="font-medium">{selectedSupplier.contact_name}</p>
                                </div>
                            )}
                            
                            <div className="flex gap-4">
                                {selectedSupplier.phone && (
                                    <div className="flex items-center gap-1 text-sm">
                                        <Phone className="w-4 h-4 text-muted-foreground" />
                                        {selectedSupplier.phone}
                                    </div>
                                )}
                                {selectedSupplier.email && (
                                    <div className="flex items-center gap-1 text-sm">
                                        <Mail className="w-4 h-4 text-muted-foreground" />
                                        {selectedSupplier.email}
                                    </div>
                                )}
                            </div>
                            
                            {(selectedSupplier.address || selectedSupplier.city) && (
                                <div className="flex items-center gap-1 text-sm">
                                    <MapPin className="w-4 h-4 text-muted-foreground" />
                                    {[selectedSupplier.address, selectedSupplier.city].filter(Boolean).join(', ')}
                                </div>
                            )}
                            
                            {selectedSupplier.stats && (
                                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold">{selectedSupplier.stats.total_orders}</div>
                                        <div className="text-xs text-muted-foreground">Commandes</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">{selectedSupplier.stats.delivery_rate}%</div>
                                        <div className="text-xs text-muted-foreground">Livraison</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-lg font-bold">{formatCurrency(selectedSupplier.stats.total_amount)}</div>
                                        <div className="text-xs text-muted-foreground">Total</div>
                                    </div>
                                </div>
                            )}
                            
                            {selectedSupplier.recent_orders?.length > 0 && (
                                <div className="pt-4 border-t">
                                    <p className="text-sm font-medium mb-2">Dernières commandes</p>
                                    <div className="space-y-2">
                                        {selectedSupplier.recent_orders.slice(0, 5).map(order => (
                                            <div key={order.id} className="flex justify-between text-sm bg-muted/30 p-2 rounded">
                                                <span>{order.order_date}</span>
                                                <span>{formatCurrency(order.total_amount)}</span>
                                                {getStatusBadge(order.status)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => handleOpenOrder(null, selectedSupplier)}>
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Commander
                        </Button>
                        <Button onClick={() => { setDetailDialog(false); handleOpenSupplier(selectedSupplier); }}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SuppliersPage;
